import { useRef, useCallback, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import showConfirmationAlert from "layouts/pages/sweet-alerts/components/WithSuccessAttachedFunction/CancelSomething";

export function useKanbanTracker(board, setBoard) {
  const { session } = useBusinessContext();
  const tracker = useRef({ added: [], moved: [], deleted: [] });
  const {
    createKanbanCard,
    moveKanbanCard,
    deleteKanban,
    SaveKanbanChanges,
    createKanbanBoard,
    reorderKanban,
  } = UseHandleKanban();

  const [toggleDeleteMode, setToggleDeleteMode] = useState(false);
  const [newColumnForm, setNewColumnForm] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState("");

  const handleToggleDeleteMode = useCallback(() => {
    setToggleDeleteMode((prev) => !prev);
  }, []);

  const handleCardMove = useCallback(
    async (updatedBoard, card, src, dest) => {
      setBoard(updatedBoard);
      if (src.fromColumnId !== dest.toColumnId) {
        tracker.current.moved.push({ id: card.id, from: src.fromColumnId, to: dest.toColumnId });
        await moveKanbanCard(
          session.selectedBusiness.id,
          card.id,
          src.fromColumnId,
          dest.toColumnId
        );
      }
    },
    [moveKanbanCard, session, setBoard]
  );

  const handleCreateCard = useCallback(
    async (columnId, formValue) => {
      if (!formValue.trim()) return;
      const newCard = { id: uuidv4(), content: formValue, columnId };

      // Track and persist
      tracker.current.added.push(newCard);
      const cardId = await createKanbanCard(session.selectedBusiness.id, {
        ...newCard,
        title: formValue,
      });

      newCard.id = cardId;
      // Update UI
      const updated = { ...board };
      const col = updated.columns.find((c) => c.id === columnId);
      col.cards.push({ ...newCard, template: null });
      setBoard(updated);
    },
    [createKanbanCard, session, board, setBoard]
  );

  const handleDeleteCard = useCallback(
    async (cardId, columnId) => {
      const updated = { ...board };
      const col = updated.columns.find((c) => c.id === columnId);
      col.cards = col.cards.filter((card) => card.id !== cardId);
      setBoard(updated);

      tracker.current.deleted.push({ id: cardId, columnId });
      await deleteKanban(session.selectedBusiness.id, cardId);
    },
    [board, deleteKanban, session, setBoard]
  );

  const handleCreateColumn = useCallback(async () => {
    if (!newColumnTitle.trim()) return;
    const columnId = await createKanbanBoard(session.selectedBusiness.id, newColumnTitle);
    const updated = { ...board };
    updated.columns.push({ id: columnId, title: newColumnTitle, cards: [] });
    setBoard(updated);
    setNewColumnForm(false);
    setNewColumnTitle("");
  }, [newColumnTitle, createKanbanBoard, session, board, setBoard]);

  const handleReorderColumns = useCallback(
    async (newBoard) => {
      setBoard(newBoard);
      const positions = newBoard.columns.reduce((acc, col, idx) => {
        acc[col.id] = idx + 1;
        return acc;
      }, {});
      await reorderKanban(session.selectedBusiness.id, positions);
    },
    [reorderKanban, session, setBoard]
  );

  const handleDeleteColumn = useCallback(
    async (columnId) => {
      try {
        const confirmed = await showConfirmationAlert({
          title: "Eliminare la colonna?",
          text: "Se elimini la colonna, eliminerai anche le schede associate ad essa.",
          icon: "warning",
          confirmButtonText: "Sì, elimina",
          cancelButtonText: "No, mantieni",
          successTitle: "Colonna eliminata",
          successText: "La colonna è stata eliminata con successo.",
        });

        if (!confirmed) return;

        const updated = { ...board };
        const columnIndex = updated.columns.findIndex((c) => c.id === columnId);

        if (columnIndex === -1) {
          throw new Error("Column not found");
        }

        const columnToDelete = updated.columns[columnIndex];

        // Track deleted cards for persistence
        if (columnToDelete && columnToDelete.cards.length > 0) {
          columnToDelete.cards.forEach((card) => {
            tracker.current.deleted.push({ id: card.id, columnId });
          });
        }

        updated.columns.splice(columnIndex, 1);
        setBoard(updated);

        await deleteKanban(session.selectedBusiness.id, columnId);

        const columnPositions = updated.columns.reduce((acc, col, idx) => {
          acc[col.id] = idx + 1;
          return acc;
        }, {});

        await reorderKanban(session.selectedBusiness.id, columnPositions);
      } catch (error) {
        console.error("Error deleting column:", error);
        const originalBoard = { ...board };
        setBoard(originalBoard);

        showConfirmationAlert({
          title: "Errore",
          text: "Si è verificato un errore durante l'eliminazione della colonna. Riprova più tardi.",
          icon: "error",
          showCancelButton: false,
          confirmButtonText: "OK",
        });
      }
    },
    [board, deleteKanban, session, setBoard, reorderKanban]
  );

  // Persist changes on unmount
  useEffect(() => {
    return () => {
      if (session?.selectedBusiness?.id) {
        SaveKanbanChanges(session.selectedBusiness.id, tracker.current);
      }
    };
  }, [session]);

  return {
    toggleDeleteMode,
    handleToggleDeleteMode,
    handleCardMove,
    handleCreateCard,
    handleDeleteCard,
    handleCreateColumn,
    handleDeleteColumn,
    handleReorderColumns,
    newColumnForm,
    setNewColumnForm,
    newColumnTitle,
    setNewColumnTitle,
  };
}
